import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import useOnClickOutside from "utils/useOnClickOutside";
import { MapEditIcon, MoreOptionsIcon } from "components/assets/Icons";
import sortAscending, { sortDescending } from "utils/sortData";

import styles from "./TrucksList.module.scss";

const TrucksList = ({ list, setList, setEditModal, setDeleteModal }) => {
  const NOTES_LENGTH = 10;
  const navigate = useNavigate()

  const ref = useRef();
  const optionsRef = useRef();
  const [showTooltip, setShowTooltip] = useState({ id: null, name: "" });
  const [showTooltipDest, setShowTooltipDest] = useState({ id: null, name: "" });
  const [showOptions, setShowOptions] = useState(null);
  const [sorted, setSorted] = useState(false);

  const trimNotes = (notes) => {
    return notes?.length > NOTES_LENGTH ? notes.slice(0, NOTES_LENGTH) + "..." : notes;
  };

  const handleClick = () => {
    setSorted((prev) => !prev);

    list.sort((a, b) => {
      return sorted ? sortDescending(a, b, "unit") : sortAscending(a, b, "unit");
    });

    setList(list);
  };

  function setHeaderHeight() {
    if (typeof window === "object") {
      return document.documentElement.style.setProperty(
        "--thead-height",
        `${ref.current?.offsetHeight}px`,
      );
    }
  }

  useEffect(() => {
    setHeaderHeight();
  }, [list]);

  useOnClickOutside(optionsRef, () => setShowOptions(null));

  return (
    <div className={styles.content}>
      <div className={styles.wrapper}>
        <table className={styles.content}>
          {!list.length ? null : (
            <>
              <thead className={styles.header} ref={ref}>
                <tr className={styles.row}>
                  <th className={styles.sorted} onClick={handleClick}>
                    Unit#
                  </th>
                  <th>Associated Users</th>
                  <th>Drivers</th>
                  <th>Dispatcher</th>
                  <th>Truck info</th>
                  <th>Special </th>
                  <th>Status & Location</th>
                  <th>Destination</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody className={styles.body}>
                {list?.map((item, index) => {
                  return (
                    <tr key={item.id} className={styles.row}>
                      <td>
                        {item.unit}
                        <div
                          className={styles.truckNotes}
                          onMouseEnter={() =>
                            setShowTooltip({ id: index, name: "truckNotes" })
                          }
                          onMouseLeave={() => setShowTooltip({ id: null, name: "" })}
                        >
                          {item.truck_note && trimNotes(item.truck_note)}
                          {item.truck_note &&
                            item.truck_note?.length > NOTES_LENGTH &&
                            showTooltip.id === index &&
                            showTooltip.name === "truckNotes" && (
                              <span className={styles.tooltip}>{item.truck_note}</span>
                            )}
                        </div>
                      </td>
                      <td>{item?.associated_users_names?.map((item) => `${item}, `)}</td>
                      <td>
                        {item.drivers?.map(({ name, phone_number }, index) => {
                          return (
                            <div key={index} className={styles.truncate}>
                              {name}
                              <br />
                              {phone_number}
                            </div>
                          );
                        })}
                      </td>
                      <td className={styles.truncate}>
                        {item.dispatcher.name}
                        <br />
                        {item.dispatcher.phone_number}
                      </td>
                      <td>
                        {item.dimensions} in
                        <br />
                        {item.payload} lb
                      </td>
                      <td>
                        <span>Air Ride - {item.air_ride ? "yes" : "no"}</span>
                        <br />
                        <span>Lift Gate - {item.lift_gate ? "yes" : "no"}</span>
                        <br />
                        <span>Pallet Jack - {item.pallet_jack ? "yes" : "no"}</span>
                        <br />
                        <span>Dock High - {item.dock_high ? "yes" : "no"}</span>
                        <br />
                      </td>
                      <td>
                        {item.availability_status && (
                          <span
                            className={classnames(
                              styles.status,
                              styles[
                                item.availability_status?.toLowerCase().replace(" ", "-")
                              ],
                            )}
                          >
                            {item.availability_status?.toLowerCase()}
                          </span>
                        )}
                        <br />
                        {item.location.city || item.location.state
                          ? `${item.location.city}, ${item.location.state} ${
                              item.location.zip_code ? item.location.zip_code : ""
                            }`
                          : ""}
                        <br />
                        {item.available_datetime}
                      </td>
                      <td>
                        {/* {item.destination.states} */}

                        <div
                          className={styles.truckNotes}
                          onMouseEnter={() => setShowTooltipDest({ id: index, name: "statesNotes" })
                          }
                          onMouseLeave={() => setShowTooltipDest({ id: null, name: "" })}
                        >
                          {item.destination.states && trimNotes(item.destination.states)}
                          {item.destination.states &&
                            item.destination.states?.length > NOTES_LENGTH &&
                            showTooltipDest.id === index &&
                            showTooltipDest.name === "statesNotes" && (
                              <span className={styles.tooltip}>{item.destination.states.replaceAll(",", ", ")}</span>
                            )}
                        </div>


                        <br />
                        <div
                          className={styles.truckNotes}
                          onMouseEnter={() =>
                            setShowTooltip({ id: index, name: "destinationNotes" })
                          }
                          onMouseLeave={() => setShowTooltip({ id: null, name: "" })}
                        >
                          {item.destination.note && trimNotes(item.destination.note)}
                          {item.destination.note &&
                            item.destination.note?.length > NOTES_LENGTH &&
                            showTooltip.id === index &&
                            showTooltip.name === "destinationNotes" && (
                              <span className={styles.tooltip}>{item.destination.note}</span>
                            )}
                        </div>
                      </td>
                      <td>
                        <button
                          className={styles.editBtn}
                          onClick={() =>
                            setEditModal({ open: true, data: { item: item } })
                          }
                        >
                          <MapEditIcon />
                        </button>
                      </td>
                      <td ref={optionsRef}>
                        {showOptions === index && (
                          <div ref={optionsRef} className={styles.options}>
                            <button className={styles.optionsItem} onClick={()=> {
                              navigate("/edit_truck", {state: {item}})
                              setShowOptions(null)
                            }}>Edit</button>
                            <button 
                              className={styles.optionsItem} 
                              onClick={() => {
                                setDeleteModal({ open: true, data: { item: item } })
                                setShowOptions(null)
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                        <button
                          className={styles.optionsBtn}
                          onClick={() => setShowOptions(index)}
                        >
                          <MoreOptionsIcon />
                        </button>

                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </>
          )}
        </table>
        {!list.length && (
          <p>There is no trucks, try to change filters</p>
        )}
      </div>
    </div>
  );
};

export default TrucksList;
